import { QUEUE_ORDER } from "./status";
import { isOverdue } from "./format";
import type { Loop, LoopStatus } from "./types";

export function activeLoops(loops: Loop[], showArchived = false): Loop[] {
  return loops.filter((l) => l.status !== "resolved" && (showArchived || l.status !== "archived"));
}

/** Ordering stands in for the backend recommendation layer. */
export function queueLoops(loops: Loop[]): Loop[] {
  return activeLoops(loops)
    .slice()
    .sort((a, b) => {
      const byStatus = QUEUE_ORDER[a.status] - QUEUE_ORDER[b.status];
      if (byStatus !== 0) return byStatus;
      const overdue = Number(isOverdue(b)) - Number(isOverdue(a));
      if (overdue !== 0) return overdue;
      if (b.weight !== a.weight) return b.weight - a.weight;
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
}

export function whyNow(loop: Loop, all: Loop[]): string {
  const unblocks = all.filter((l) =>
    l.dependencies.some((d) => d.loopId === loop.id && d.relation === "blocked_by"),
  );
  const parts: string[] = [];
  if (isOverdue(loop) && loop.expectedDate) {
    const days = Math.max(
      1,
      Math.round((Date.now() - new Date(loop.expectedDate).getTime()) / 86_400_000),
    );
    parts.push(`the response was expected ${days} day${days === 1 ? "" : "s"} ago`);
  }
  if (loop.status === "ready_to_verify")
    parts.push("everything is done except confirming the result");
  if (loop.status === "blocked") {
    const blocker = loop.dependencies.find((d) => d.relation === "blocked_by");
    parts.push(
      blocker
        ? `it cannot move until ${blocker.note.toLowerCase()}`
        : "it is waiting on something else first",
    );
  }
  if (unblocks.length) {
    parts.push(
      `resolving it will unblock ${unblocks.length} other matter${unblocks.length === 1 ? "" : "s"}`,
    );
  }
  if (!parts.length) return loop.whyNow;
  const sentence = parts.join(", and ");
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
}

export function counterparties(loops: Loop[]): string[] {
  return Array.from(
    new Set(loops.map((l) => l.counterparty).filter((c): c is string => Boolean(c))),
  ).sort();
}

export function statusCounts(loops: Loop[]): Record<LoopStatus, number> {
  const base = {
    open: 0,
    waiting: 0,
    action_needed: 0,
    blocked: 0,
    ready_to_verify: 0,
    resolved: 0,
    archived: 0,
  } as Record<LoopStatus, number>;
  for (const l of loops) base[l.status] += 1;
  return base;
}

export function dependencyLoops(loop: Loop, all: Loop[]) {
  return loop.dependencies
    .map((d) => ({ dependency: d, loop: all.find((l) => l.id === d.loopId) }))
    .filter((x): x is { dependency: (typeof loop.dependencies)[number]; loop: Loop } =>
      Boolean(x.loop),
    );
}
